/**
 * The galaxy: IPM's first twenty-eight planets (GDD §3.1) — same prices,
 * distances and ore mixes — named after IdleVoid's hulls and Armory systems. In the shared fiction
 * the hulls were named after the worlds the miners worked.
 *
 * Upgrade curves are IPM's own (from the IPM wiki's Planetary Upgrades page):
 *   mining   0.25 + 0.1(L−1) + 0.017(L−1)²   ore/s
 *   ship     1 + 0.2(L−1) + (L−1)²/75       speed
 *   cargo    5 + 2(L−1) + 0.1(L−1)²          units
 *   price    unlock/20 × 1.3^(L−1)           GC to go from L to L+1
 */

export interface OreShare {
  readonly id: string;
  readonly share: number;
}

export interface PlanetDef {
  readonly index: number;
  readonly id: string;
  readonly name: string;
  /** The IPM planet this one is. */
  readonly ipm: string;
  readonly ores: readonly OreShare[];
  /** IPM distance; one ship leg takes distance ÷ ship speed seconds. */
  readonly distance: number;
  /** GC to unlock (IPM base price). */
  readonly unlockCost: number;
  /** Render radius in galaxy units. */
  readonly size: number;
}

type Row = readonly [string, string, string, readonly OreShare[], number, number, number];

const s = (id: string, share: number): OreShare => ({ id, share });

const ROWS: readonly Row[] = [
  ['wayfinder', 'Wayfinder', 'Balor', [s('ferrite', 1)], 10, 100, 12],
  ['cinderfall', 'Cinderfall', 'Drasta', [s('ferrite', 0.8), s('cuprite', 0.2)], 12, 200, 13],
  ['glasshollow', 'Glasshollow', 'Anadius', [s('ferrite', 0.5), s('cuprite', 0.5)], 14, 500, 13],
  ['cobalt_reach', 'Cobalt Reach', 'Dholen', [s('cuprite', 0.8), s('silica', 0.2)], 15, 1250, 14],
  ['lumen', 'Lumen', 'Verr', [s('silica', 0.5), s('cuprite', 0.3), s('ferrite', 0.2)], 16, 5e3, 14],
  ['titans_rest', "Titan's Rest", 'Newton', [s('silica', 1)], 18, 9e3, 17],
  ['cryo_lattice', 'Cryo Lattice', 'Widow', [s('cuprite', 0.4), s('ferrite', 0.4), s('cobaltite', 0.2)], 20, 15e3, 15],
  ['ember_vanguard', 'Ember Vanguard', 'Acheron', [s('cobaltite', 0.6), s('ferrite', 0.4)], 22, 25e3, 16],
  ['chronicle', 'Chronicle', 'Yangtze', [s('cobaltite', 0.8), s('lumen', 0.2)], 23, 40e3, 15],
  ['prism_shard', 'Prism Shard', 'Solveig', [s('lumen', 0.5), s('cobaltite', 0.3), s('silica', 0.2)], 25, 75e3, 14],
  ['umbral_eclipse', 'Umbral Eclipse', 'Imir', [s('lumen', 1)], 26, 150e3, 18],
  ['event_horizon', 'Event Horizon', 'Relic', [s('silica', 0.45), s('cobaltite', 0.35), s('titanite', 0.2)], 28, 250e3, 19],
  ['foundry_bastion', 'Foundry Bastion', 'Nith', [s('titanite', 0.8), s('lumen', 0.2)], 30, 400e3, 17],
  ['anomaly_bloom', 'Anomaly Bloom', 'Batalla', [s('ferrite', 0.4), s('cuprite', 0.4), s('cryonite', 0.2)], 33, 800e3, 16],
  ['starweave', 'Starweave', 'Micah', [s('cryonite', 0.5), s('titanite', 0.5)], 35, 1.5e6, 17],
  ['solar_crown', 'Solar Crown', 'Pranas', [s('cryonite', 1)], 37, 3e6, 19],
  ['rift_warden', 'Rift Warden', 'Castellus', [s('lumen', 0.4), s('cobaltite', 0.35), s('emberite', 0.25)], 40, 6e6, 18],
  ['tidal_helix', 'Tidal Helix', 'Gorgon', [s('emberite', 0.8), s('silica', 0.2)], 43, 12e6, 17],
  ['nova_reactor', 'Nova Reactor', 'Parnitha', [s('cryonite', 0.7), s('chronite', 0.3)], 45, 25e6, 19],
  ['grand_orrery', 'Grand Orrery', 'Orisoni', [s('chronite', 0.7), s('emberite', 0.3)], 48, 50e6, 21],
  ['aegis_bulwark', 'Aegis Bulwark', 'Theseus', [s('chronite', 1)], 51, 100e6, 20],
  ['signal_spire', 'Signal Spire', 'Zelene', [s('titanite', 0.7), s('prismite', 0.3)], 54, 200e6, 17],
  ['hive_cluster', 'Hive Cluster', 'Han', [s('prismite', 0.7), s('emberite', 0.2), s('cryonite', 0.1)], 57, 400e6, 19],
  ['tempest_coil', 'Tempest Coil', 'Strennus', [s('prismite', 0.7), s('chronite', 0.3)], 58, 800e6, 18],
  ['thornbloom', 'Thornbloom', 'Osun', [s('lumen', 0.6), s('umbralite', 0.4)], 60, 1.6e9, 20],
  ['void_mirror', 'Void Mirror', 'Ploitari', [s('umbralite', 0.5), s('emberite', 0.5)], 63, 3.2e9, 19],
  ['quantum_ghost', 'Quantum Ghost', 'Elysta', [s('umbralite', 1)], 67, 6.4e9, 21],
  ['null_interceptor', 'Null Interceptor', 'Tikkun', [s('umbralite', 0.4), s('prismite', 0.35), s('voidglass', 0.25)], 70, 12.5e9, 22],
];

export const PLANETS: readonly PlanetDef[] = ROWS.map(([id, name, ipm, ores, distance, unlockCost, size], index) => ({
  index, id, name, ipm, ores, distance, unlockCost, size,
}));

export type PlanetStat = 'mine' | 'ship' | 'cargo';

export const PLANET_STATS: readonly PlanetStat[] = ['mine', 'ship', 'cargo'];

export const PLANET_STAT_LABEL: Readonly<Record<PlanetStat, string>> = {
  mine: 'Mining',
  ship: 'Ship speed',
  cargo: 'Cargo',
};

export function baseMineRate(level: number): number {
  const n = level - 1;
  return 0.25 + 0.1 * n + 0.017 * n * n;
}

export function baseShipSpeed(level: number): number {
  const n = level - 1;
  return 1 + 0.2 * n + (n * n) / 75;
}

export function baseCargo(level: number): number {
  const n = level - 1;
  return 5 + 2 * n + 0.1 * n * n;
}

/** All three planet upgrades share IPM's price curve. */
export const UPGRADE_PRICE_FRACTION = 1 / 20;
export const UPGRADE_PRICE_GROWTH = 1.3;

/** GC you start a galaxy with: enough for the first planet, as in IPM. */
export const STARTING_GC = 100;

/** Planets visible before any Telescope; each Telescope project reveals three more. */
export const BASE_VISIBLE_PLANETS = 4;
export const PLANETS_PER_TELESCOPE = 3;

/** IPM sells a galaxy from $10M of Galaxy Value. */
export const FOLD_MIN_GALAXY_VALUE = 10e6;
